package org.example.project.enginevertrieb

import androidx.compose.runtime.*
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.example.project.dashboard.PopupEngineCard
import org.example.project.dashboard.POPUP_ENGINE_MODAL_Z_INDEX
import org.example.project.settings.*
import org.example.project.enginekalender.terminInputStyle
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event

@Composable
internal fun FlowSettings(section: String, onClose: () -> Unit) {
    val editingLabels = section == "kpi-labels"
    val scope = rememberCoroutineScope()
    val sharedKpis = LocalLinkedInKpis.current
    var labels by remember { mutableStateOf<Map<String, String>>(emptyMap()) }
    var days by remember { mutableStateOf<Map<String, String>>(emptyMap()) }
    var busy by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf("") }
    var labelVersion by remember { mutableStateOf(0) }
    val phases = visibleSalesCheckpoints
    val followUps = phases.filter { checkpointHasFollowUp(it.id) }

    fun update(data: All) {
        labels = data.vertriebCheckpointLabels
        days = followUps.associate { it.id to (data.vertriebCheckpointFristen[it.id] ?: 4).toString() }
        sharedKpis?.deadlines = data.vertriebCheckpointFristen
        labelVersion++
    }
    fun saveLabels(reset: Boolean = false) {
        if (busy) return
        val values = if (reset) emptyMap() else labels.mapValues { it.value.trim() }
            .filter { (id, label) -> label.isNotBlank() && label != phases.firstOrNull { it.id == id }?.defaultLabel }
        busy = true; error = ""
        scope.launch {
            try {
                val saved = LinkedInFlowApi.saveCheckpointLabels(values)
                labels = saved.vertriebCheckpointLabels; labelVersion++
                LinkedInSyncState.version++
            } catch (e: CancellationException) { throw e }
            catch (e: Exception) { error = e.message ?: "KPI-Namen konnten nicht gespeichert werden." }
            finally { busy = false }
        }
    }
    fun saveDays(reset: Boolean = false) {
        if (busy) return
        val values = if (reset) followUps.associate { it.id to 4 } else days.mapValues { it.value.toInt() }
        busy = true; error = ""
        scope.launch {
            try {
                val saved = LinkedInFlowApi.saveCheckpointDeadlines(values)
                days = followUps.associate { it.id to (saved.vertriebCheckpointFristen[it.id] ?: 4).toString() }
                sharedKpis?.deadlines = saved.vertriebCheckpointFristen
                LinkedInSyncState.version++
            } catch (e: CancellationException) { throw e }
            catch (e: Exception) { error = e.message ?: "Follow-up-Fristen konnten nicht gespeichert werden." }
            finally { busy = false }
        }
    }
    LaunchedEffect(Unit) {
        try { update(LinkedInFlowApi.settings()) }
        catch (e: CancellationException) { throw e }
        catch (e: Exception) { error = e.message ?: "Flow-Einstellungen konnten nicht geladen werden." }
        finally { busy = false }
    }
    PopupEngineCard(windowKey = "vertrieb-flow-settings-$section", zIndex = POPUP_ENGINE_MODAL_Z_INDEX,
        widthPx = if (editingLabels) 1060 else 860, heightPx = 660, minWidthPx = 420, minHeightPx = 420,
        centered = true, viewportAnchored = true, resizable = true, onClose = { if (!busy) onClose() }) {
        Div({ style {
            height(100.percent); minHeight(0.px); boxSizing("border-box"); padding(32.px)
            display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(32.px)
            color(UISettings.STATE_BUTTON_TEXT)
        } }) {
            Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(14.px); flexShrink(0); textAlign("center") } }) {
                H2({ style { margin(0.px); fontSize(22.px); fontWeight("600") } }) {
                    Text(if (editingLabels) "KPI-Bezeichnungen anpassen" else "Follow-up-Fristen festlegen")
                }
                P({ style { margin(0.px); fontSize(14.px); lineHeight("22px"); opacity(.7) } }) {
                    Text(if (editingLabels) "Klicke auf eine Bezeichnung, um sie umzubenennen."
                        else "Lege fest, nach wie vielen Tagen ein Follow-up vorgeschlagen wird.")
                }
            }
            if (error.isNotBlank()) Div({ attr("role", "alert"); style { color(UISettings.ACCENT); textAlign("center"); flexShrink(0) } }) { Text(error) }
            Div({ style { flex("1"); minHeight(0.px); overflowY("auto"); padding(0.px, 4.px) } }) {
                Div({ style {
                    display(DisplayStyle.Grid); width(100.percent); gap(20.px)
                    gridTemplateColumns(if (editingLabels) "repeat(auto-fit, minmax(min(100%, 190px), 1fr))"
                        else "repeat(auto-fit, minmax(min(100%, 220px), 1fr))")
                    alignItems(AlignItems.Start)
                } }) {
                    if (editingLabels) phases.forEach { checkpoint ->
                        key(checkpoint.id, labelVersion) {
                            var target by remember { mutableStateOf<HTMLElement?>(null) }
                            val initial = labels[checkpoint.id] ?: checkpoint.defaultLabel
                            Div({
                                onClick { if (!busy) target?.focus() }
                                style {
                                    display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(14.px)
                                    minWidth(0.px); minHeight(88.px); padding(16.px); boxSizing("border-box")
                                    borderRadius(8.px); backgroundColor(Color(UISettings.SALES_KPI_BG)); cursor("text")
                                }
                            }) {
                                Img("images/${checkpoint.icon}.svg", attrs = { attr("alt", ""); style {
                                    width(26.px); height(26.px); flexShrink(0); property("filter", "brightness(0) invert(1)")
                                } })
                                Div({
                                    attr("contenteditable", if (busy) "false" else "plaintext-only")
                                    attr("role", "textbox"); attr("aria-label", "KPI-Name ${checkpoint.defaultLabel}"); attr("spellcheck", "false")
                                    ref { element ->
                                        target = element; element.textContent = initial
                                        val input: (Event) -> Unit = { labels = labels + (checkpoint.id to element.textContent.orEmpty()) }
                                        element.addEventListener("input", input)
                                        onDispose { element.removeEventListener("input", input); target = null }
                                    }
                                    onKeyDown { if (it.key == "Enter") { it.preventDefault(); target?.blur() } }
                                    style {
                                        flex("1"); minWidth(0.px); minHeight(20.px); fontSize(14.px); lineHeight("22px")
                                        textAlign("center"); color(UISettings.STATE_BUTTON_TEXT); border(0.px)
                                        property("outline", "none"); backgroundColor(Color.transparent); cursor("text"); property("overflow-wrap", "anywhere")
                                    }
                                })
                            }
                        }
                    } else followUps.forEach { checkpoint ->
                        Label(attrs = { style {
                            minWidth(0.px); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column)
                            gap(12.px); padding(16.px); borderRadius(8.px); backgroundColor(Color(UISettings.SALES_KPI_BG))
                        } }) {
                            Span({ style { fontSize(14.px); lineHeight("22px") } }) { Text(checkpoint.label) }
                            Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(12.px) } }) {
                                Input(InputType.Text, attrs = {
                                    value(days[checkpoint.id].orEmpty()); attr("inputmode", "numeric"); attr("pattern", "[0-9]*")
                                    attr("maxlength", "3"); attr("aria-label", "Follow-up-Tage ${checkpoint.label}")
                                    if (busy) disabled()
                                    onInput { days = days + (checkpoint.id to it.value.filter { char -> char in '0'..'9' }.take(3)) }
                                    style { terminInputStyle(); minWidth(0.px); height(42.px); textAlign("center") }
                                })
                                Span({ style { fontSize(13.px); opacity(.7); flexShrink(0) } }) { Text("Tage") }
                            }
                        }
                    }
                }
            }
            Div({ style { display(DisplayStyle.Flex); flexWrap(FlexWrap.Wrap); justifyContent(JustifyContent.Center); gap(16.px); flexShrink(0) } }) {
                val valid = if (editingLabels) labels.values.all { it.trim().length <= 80 }
                    else days.size == followUps.size && days.values.all { (it.toIntOrNull() ?: 0) in 1..365 }
                LumaticaButton("Speichern", { if (editingLabels) saveLabels() else saveDays() }, enabled = !busy && valid, variant = LumaticaButtonVariant.Action)
                LumaticaButton("Zurücksetzen", { if (editingLabels) saveLabels(true) else saveDays(true) }, enabled = !busy)
            }
        }
    }
}
